import { useEffect, useState, useRef } from "react";

export default function OrderSelector({ quote, selected, onSelect }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Track retry attempts for token availability
  const retryCountRef = useRef(0);
  const maxRetries = 10;
  const retryDelayMs = 500;

  useEffect(() => {
    if (!quote || !quote.quoteNumber) {
      setOrders([]);
      return;
    }

    const controller = new AbortController();
    let retryTimeout = null;

    async function fetchOrders() {
      setLoading(true);
      setError("");

      try {
        const accessToken = sessionStorage.getItem("msal.backendToken");

        if (!accessToken) {
          // Token not yet available - retry after delay
          if (retryCountRef.current < maxRetries) {
            retryCountRef.current += 1;
            console.log(`⏳ OrderSelector: Waiting for backend token... (attempt ${retryCountRef.current}/${maxRetries})`);
            setLoading(false);
            retryTimeout = setTimeout(fetchOrders, retryDelayMs);
            return;
          }
          // Max retries reached
          setError("No backend token available. Please refresh the page.");
          setLoading(false);
          return;
        }

        // Reset retry count on success
        retryCountRef.current = 0;

        const url = `/api/orders?quoteNumber=${encodeURIComponent(
          quote.quoteNumber
        )}`;

        const response = await fetch(url, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || "Failed to load orders.");
        }

        const data = await response.json();
        const loadedOrders = Array.isArray(data.orders) ? data.orders : [];
        setOrders(loadedOrders);

        console.log("📦 OrderSelector: Loaded", loadedOrders.length, "orders for quote", quote.quoteNumber);

        // Check if there's a pending order to auto-select (from MyWork navigation)
        const pendingOrder = sessionStorage.getItem("3dcolor-pending-order");
        console.log("📦 OrderSelector: pendingOrder from sessionStorage:", pendingOrder);

        if (pendingOrder && loadedOrders.length > 0) {
          const targetOrder = loadedOrders.find(o => String(o.orderNumber) === String(pendingOrder));
          if (targetOrder) {
            console.log("📍 Auto-selecting pending order:", pendingOrder);
            onSelect(targetOrder);
          } else {
            console.log("📍 Pending order not found. Available orders:", loadedOrders.map(o => o.orderNumber));
          }
          sessionStorage.removeItem("3dcolor-pending-order");
        } else if (loadedOrders.length === 1) {
          // Auto-select when there's only one order
          console.log("📍 Auto-selecting single order:", loadedOrders[0].orderNumber);
          onSelect(loadedOrders[0]);
        } else {
          console.log("📦 OrderSelector: No auto-select (orders:", loadedOrders.length, ", pendingOrder:", pendingOrder, ")");
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Order fetch error:", err);
          setError("Unable to load orders.");
        }
      } finally {
        setLoading(false);
      }
    }

    fetchOrders();
    return () => {
      controller.abort();
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [quote, onSelect]);

  // Format due date
  function formatDate(dateStr) {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  }

  // Get status badge config
  function getStatusBadge(status) {
    const normalizedStatus = (status || "").toLowerCase();
    if (normalizedStatus === "active" || normalizedStatus === "open") {
      return { text: "Active", color: "bg-green-600 text-gray-900" };
    }
    if (normalizedStatus === "closed" || normalizedStatus === "complete") {
      return { text: "Closed", color: "bg-slate-200 text-gray-800" };
    }
    if (normalizedStatus === "on hold" || normalizedStatus === "hold") {
      return { text: "On Hold", color: "bg-yellow-600 text-gray-900" };
    }
    if (normalizedStatus === "cancelled") {
      return { text: "Cancelled", color: "bg-red-600 text-gray-900" };
    }
    return { text: status || "Unknown", color: "bg-gray-600 text-gray-800" };
  }

  // Sort orders: Active first, then by due date
  const sortedOrders = [...orders].sort((a, b) => {
    const statusA = (a.status || "").toLowerCase();
    const statusB = (b.status || "").toLowerCase();

    // Active/Open orders first
    const isActiveA = statusA === "active" || statusA === "open";
    const isActiveB = statusB === "active" || statusB === "open";
    if (isActiveA && !isActiveB) return -1;
    if (!isActiveA && isActiveB) return 1;

    // Then sort by due date
    const dateA = a.dueDate ? new Date(a.dueDate) : new Date("9999-12-31");
    const dateB = b.dueDate ? new Date(b.dueDate) : new Date("9999-12-31");
    return dateA - dateB;
  });

  return (
    <div className="flex flex-col w-full h-[200px] text-gray-900">
      {/* Label */}
      <div className="flex-none mb-2 flex items-center justify-between">
        <label className="text-sm font-semibold">Orders</label>
        {orders.length > 0 && (
          <span className="text-xs text-gray-500">{orders.length} order{orders.length !== 1 ? "s" : ""}</span>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex-none text-xs text-gray-500 italic">
          Loading orders…
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex-none text-xs text-red-500">{error}</div>
      )}

      {/* Orders List */}
      <div
        className="
          flex-1 overflow-y-auto
          bg-slate-200 border border-gray-200 rounded-lg
          divide-y divide-gray-100
        "
      >
        {!loading && orders.length === 0 && (
          <div className="p-3 text-xs text-gray-500">
            No orders found for this quote.
          </div>
        )}

        {sortedOrders.map((order) => {
          // Use uniqueID if available, otherwise fall back to orderNumber
          const orderId = order.uniqueID || order.orderNumber;
          const isActive =
            selected && (String(selected.uniqueID) === String(orderId) || String(selected.orderNumber) === String(order.orderNumber));

          const pm =
            order.projectManager ||
            order.user_Text1 ||
            "No PM";

          const statusBadge = getStatusBadge(order.status);

          return (
            <button
              key={orderId}
              onClick={() => onSelect(order)}
              className={`
                w-full text-left px-3 py-3 text-sm md:text-base transition
                min-h-[60px]
                ${isActive
                  ? "bg-purple-50 border-l-3 border-purple-500"
                  : "hover:bg-slate-100 border-l-3 border-transparent"}
              `}
            >
              {/* Top row: Order # + Status badge */}
              <div className="flex items-center justify-between gap-2">
                <span className="text-gray-900 font-semibold">
                  Order #{order.orderNumber}
                </span>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${statusBadge.color}`}>
                  {statusBadge.text}
                </span>
              </div>

              {/* Due Date */}
              <div className="text-xs mt-1 text-gray-600">
                Due: {formatDate(order.dueDate)}
              </div>

              {/* PM + Job Count */}
              <div className="text-xs text-gray-500 mt-1 flex justify-between">
                <span>PM: {pm}</span>
                <span>{order.jobCount ?? "?"} jobs</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
